/**
 * One-way SHA-256 of a personal value (email, name, address) so the plain
 * value never reaches OpenPanel (HB-1296).
 *
 * The recipe is a contract shared with the ClickHouse backfill and with CDP,
 * who hash their own copy of the data to join on it — any drift produces a
 * different hash and the join silently fails:
 *
 *   trim → lowercase → UTF-8 → SHA-256 → lowercase hex
 *
 * Resolves to `undefined` for an empty value, and when WebCrypto is unavailable
 * (`crypto.subtle` only exists in secure contexts), so callers drop the value
 * rather than ever falling back to sending it in plain text.
 */
export const hashPii = async (value: string | null | undefined): Promise<string | undefined> => {
	const normalised = value?.trim().toLowerCase();

	if (!normalised) {
		return undefined;
	}

	try {
		const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalised));

		return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(
			''
		);
	} catch {
		// Analytics plumbing must never surface an error to the user.
		return undefined;
	}
};
